// import React, { useEffect, useState } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { useResetPasswordMutation } from "../../redux/api/userApi";
// import toast from "react-hot-toast";
// import { useSelector } from "react-redux";

// const ResetPassword = () => {
//   const params = useParams(); // From URL: /password/reset/:token
//   const navigate = useNavigate();

//   const [password, setPassword] = useState("");
//   const [confirmPassword, setConfirmPassword] = useState("");

//   const [resetPassword, { isLoading, error, isSuccess }] = useResetPasswordMutation();
//   const { isAuthenticated } = useSelector((state) => state.auth);

//   useEffect(() => {
//     if (isAuthenticated) {
//       navigate("/");
//     }
//     if (error) {
//       toast.error(error?.data?.message);
//     }
//     if(isSuccess){
//         toast.success("Password reset successfully");
//         navigate("/login");
//     }
//   }, [error, isAuthenticated, isSuccess, navigate]);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if(password !== confirmPassword){
//         return toast.error("Passwords do not match")
//     }
//     const data = { password, confirmPassword };

//     resetPassword({ token: params?.token, body: data });
//   };

//   return (
//     <section className="max-w-md mx-auto my-20 px-6 py-10 bg-[#f5f5dc] shadow-md rounded-md">
//       <h2 className="text-2xl font-playfair text-amber-700 mb-4 text-center">
//         Reset Password
//       </h2>
//       <p className="text-sm font-sourcesans text-stone-600 text-center mb-6">
//         Enter your new password below to reset your account.
//       </p>

//       <form onSubmit={handleSubmit} className="space-y-6">
//         {/* New Password */}
//         <div>
//           <label
//             htmlFor="password"
//             className="block text-sm font-sourcesans text-stone-700 mb-1"
//           >
//             New Password
//           </label>
//           <input
//             type="password"
//             id="password"
//             required
//             value={password}
//             onChange={(e) => setPassword(e.target.value)}
//             className="w-full px-4 py-2 border border-stone-300 bg-white text-stone-800 placeholder-stone-500 font-sourcesans rounded-md focus:outline-none focus:ring-amber-600 focus:border-amber-600"
//             placeholder="Enter new password"
//           />
//         </div>

//         {/* Confirm Password */}
//         <div>
//           <label
//             htmlFor="confirmPassword"
//             className="block text-sm font-sourcesans text-stone-700 mb-1"
//           >
//             Confirm Password
//           </label>
//           <input
//             type="password"
//             id="confirmPassword"
//             required
//             value={confirmPassword}
//             onChange={(e) => setConfirmPassword(e.target.value)}
//             className="w-full px-4 py-2 border border-stone-300 bg-white text-stone-800 placeholder-stone-500 font-sourcesans rounded-md focus:outline-none focus:ring-amber-600 focus:border-amber-600"
//             placeholder="Re-enter new password"
//           />
//         </div>

//         {/* Submit Button */}
//         <div>
//           <button
//             type="submit"
//             disabled={isLoading}
//             className="w-full bg-amber-700 hover:bg-amber-800 text-white px-6 py-2 rounded-md text-sm font-sourcesans focus:outline-none focus:ring-2 focus:ring-amber-600"
//           >
//             {isLoading ? "Resetting..." : "Reset Password"}
//           </button>
//         </div>
//       </form>
//     </section>
//   );
// };

// export default ResetPassword;

import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useResetPasswordMutation } from "../../redux/api/userApi";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

const useParticles = (canvasRef) => {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId, frame = 0;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    const particles = Array.from({ length: 38 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.5 + 0.3,
      speed: Math.random() * 0.00015 + 0.00005,
      opacity: Math.random() * 0.18 + 0.04,
      phase: Math.random() * Math.PI * 2,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;
      particles.forEach((p) => {
        const x = ((p.x + frame * p.speed) % 1) * canvas.width;
        const y = p.y * canvas.height;
        const pulse = Math.sin(frame * 0.012 + p.phase) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.arc(x, y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(188, 63, 74, ${p.opacity * pulse})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, [canvasRef]);
};

const ResetPassword = () => {
  const params = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  useParticles(canvasRef);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const [resetPassword, { isLoading, error, isSuccess }] = useResetPasswordMutation();
  const { isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated) navigate("/");
    if (error) toast.error(error?.data?.message);
    if (isSuccess) {
      toast.success("Password reset successfully");
      navigate("/login");
    }
  }, [error, isAuthenticated, isSuccess, navigate]);

  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setPassword(val);
    setPasswordStrength({
      hasMinLength: val.length >= 8,
      hasUpperCase: /[A-Z]/.test(val),
      hasLowerCase: /[a-z]/.test(val),
      hasNumber: /\d/.test(val),
      hasSpecialChar: /[@$!%*?&#]/.test(val),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password !== confirmPassword) return toast.error("Passwords do not match");
    const isPasswordValid = Object.values(passwordStrength).every(Boolean);
    if (!isPasswordValid) return toast.error("Please meet all password requirements");
    resetPassword({ token: params?.token, body: { password, confirmPassword } });
  };

  const isPasswordValid = Object.values(passwordStrength).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword !== "";

  const requirements = [
    { key: "hasMinLength", label: "At least 8 characters" },
    { key: "hasUpperCase", label: "One uppercase letter" },
    { key: "hasLowerCase", label: "One lowercase letter" },
    { key: "hasNumber", label: "One number" },
    { key: "hasSpecialChar", label: "One special character (@$!%*?&#)" },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <>
      <Helmet>
        <title>Reset Password — Sanguine Accent</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="relative min-h-screen bg-[#f5f3ea] flex items-center justify-center px-4 py-12 overflow-hidden">

        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#BC3F4A]/40 to-transparent" />
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" />

        <motion.div
          className="relative z-10 max-w-md w-full space-y-6 bg-white/70 backdrop-blur-sm p-8 rounded-lg shadow-sm"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <div className="text-center space-y-1">
            <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sourcesans">Account</p>
            <h1 className="text-3xl font-playfair font-bold text-[#BC3F4A]">Reset Password</h1>
          </div>

          <div className="w-12 h-px bg-[#BC3F4A]/40 mx-auto" />

          <p className="text-sm font-sourcesans text-stone-500 text-center">
            Enter your new password below to reset your account.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* New Password */}
            <div>
              <input
                type="password"
                id="password"
                required
                value={password}
                onChange={handlePasswordChange}
                placeholder="New password"
                className="w-full px-4 py-3 border border-[#e2e1d8] bg-white text-[#1c1917] placeholder-stone-400 font-sourcesans rounded-md focus:outline-none focus:border-[#BC3F4A] transition text-sm"
              />
              {password && (
                <div className="mt-3 p-3 bg-stone-50 rounded-md space-y-1">
                  {requirements.map(({ key, label }) => (
                    <p key={key} className={`text-xs font-sourcesans ${passwordStrength[key] ? "text-green-600" : "text-stone-400"}`}>
                      {passwordStrength[key] ? "✓" : "○"} {label}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <input
                type="password"
                id="confirmPassword"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full px-4 py-3 border border-[#e2e1d8] bg-white text-[#1c1917] placeholder-stone-400 font-sourcesans rounded-md focus:outline-none focus:border-[#BC3F4A] transition text-sm"
              />
              {confirmPassword && (
                <p className={`mt-2 text-xs font-sourcesans ${passwordsMatch ? "text-green-600" : "text-red-500"}`}>
                  {passwordsMatch ? "✓ Passwords match" : "○ Passwords do not match"}
                </p>
              )}
            </div>

            {error && (
              <p className="text-red-600 text-sm font-sourcesans">{error.data?.message}</p>
            )}

            <button
              type="submit"
              disabled={isLoading || !isPasswordValid || !passwordsMatch}
              className="w-full py-3 px-4 bg-[#BC3F4A] hover:bg-[#a3323b] text-white font-playfair rounded-md transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </button>

            {password && (!isPasswordValid || !passwordsMatch) && (
              <p className="text-xs text-center font-sourcesans text-stone-400">
                {!isPasswordValid ? "Please meet all password requirements" : "Please ensure passwords match"}
              </p>
            )}
          </form>

          <p className="text-center text-[10px] uppercase tracking-[0.2em] text-stone-400 font-sourcesans pt-2">
            Make it. Mean it.
          </p>
        </motion.div>

        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#BC3F4A]/40 to-transparent" />
      </div>
    </>
  );
};

export default ResetPassword;